import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SwapOutlined,
  HeartOutlined,
  HeartFilled,
  LoadingOutlined,
  PauseOutlined,
  CaretRightOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import {
  attemptAddSongToFavs,
  attemptDeleteSongFromFavs,
  attemptGetFavs,
} from '../../utils/redux/favsSlice'
import './GlobalAudioPlayer.scss'

type RepeatMode = 'off' | 'all' | 'one'

export const GlobalAudioPlayer: React.FC = () => {
  const {
    currentSong,
    queue,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
    playQueue,
  } = useAudioPlayer()

  const dispatch = useAppDispatch()
  const favSongs = useAppSelector((s: any) => s.favs?.songs ?? [])

  const [queueOpen, setQueueOpen] = useState(false)
  const [shuffle, setShuffle] = useState(false)
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('off')
  const [isMobile, setIsMobile] = useState(false)

  const autoGuardRef = useRef<{ songId?: string; stamp?: number }>({})
  const shuffleOrderRef = useRef<number[]>([])
  const shufflePosRef = useRef<number>(0)
  const shuffleSigRef = useRef<string>('')

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  const safeQueue = useMemo(
    () => (queue?.length ? queue : currentSong ? [currentSong] : []),
    [queue, currentSong],
  )

  const queueSig = useMemo(
    () => safeQueue.map((s: any) => String(s?._id ?? '')).join('|'),
    [safeQueue],
  )

  const currentSongId = String((currentSong as any)?._id ?? '')

  const realIndex = useMemo(() => {
    if (!safeQueue.length || !currentSongId) return Math.max(0, currentIndex ?? 0)
    const idx = safeQueue.findIndex((s: any) => String(s?._id ?? '') === currentSongId)
    return idx >= 0 ? idx : Math.max(0, currentIndex ?? 0)
  }, [safeQueue, currentSongId, currentIndex])

  const isSongLiked = useMemo(() => {
    if (!currentSongId) return false
    return (favSongs || []).some((s: any) => String(s?._id) === currentSongId)
  }, [favSongs, currentSongId])

  const progress = useMemo(() => {
    if (!duration || duration <= 0) return 0
    return Math.max(0, Math.min(1, currentTime / duration))
  }, [currentTime, duration])

  const controlsDisabled = isLoading
  const canSeek = !isLoading && duration > 0

  const playByIndex = useCallback(
    (idx: number) => {
      if (!safeQueue.length) return
      const clamped = Math.max(0, Math.min(idx, safeQueue.length - 1))
      playQueue(safeQueue, clamped)
    },
    [safeQueue, playQueue],
  )

  const shuffleArray = (arr: number[]) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

  const buildShuffleOrder = useCallback((len: number, startIdx: number) => {
    const all = Array.from({ length: len }, (_, i) => i)
    const rest = all.filter((i) => i !== startIdx)
    shuffleArray(rest)
    return [startIdx, ...rest]
  }, [])

  const ensureShuffleOrder = useCallback(() => {
    const len = safeQueue.length

    if (!shuffle || len <= 1) {
      shuffleOrderRef.current = []
      shufflePosRef.current = 0
      shuffleSigRef.current = ''
      return
    }

    const cur = Math.max(0, Math.min(realIndex, len - 1))
    const order = shuffleOrderRef.current

    const needRebuild =
      !order.length || order.length !== len || shuffleSigRef.current !== queueSig

    if (needRebuild) {
      shuffleOrderRef.current = buildShuffleOrder(len, cur)
      shuffleSigRef.current = queueSig
    }

    let pos = shuffleOrderRef.current.indexOf(cur)
    if (pos < 0) {
      shuffleOrderRef.current = buildShuffleOrder(len, cur)
      shuffleSigRef.current = queueSig
      pos = 0
    }

    shufflePosRef.current = pos
  }, [shuffle, safeQueue.length, realIndex, buildShuffleOrder, queueSig])

  useEffect(() => {
    ensureShuffleOrder()
  }, [ensureShuffleOrder])

  const cycleRepeat = useCallback(() => {
    if (controlsDisabled) return
    seek(0)
    if (!isPlaying) togglePlayPause()
    setRepeatMode((m) => (m === 'off' ? 'all' : m === 'all' ? 'one' : 'off'))
  }, [controlsDisabled, seek, isPlaying, togglePlayPause])

  const toggleLikeSong = useCallback(async () => {
    if (controlsDisabled) return
    if (!currentSongId) return
    try {
      if (isSongLiked) {
        await dispatch(attemptDeleteSongFromFavs(currentSongId)).unwrap()
      } else {
        await dispatch(attemptAddSongToFavs(currentSongId)).unwrap()
      }
      dispatch(attemptGetFavs())
    } catch (e) {}
  }, [controlsDisabled, currentSongId, isSongLiked, dispatch])

  const goNext = useCallback(
    (fromAutoEnd?: boolean) => {
      if (isLoading) return
      if (!safeQueue.length) return

      if (shuffle && safeQueue.length > 1) {
        ensureShuffleOrder()
        const order = shuffleOrderRef.current
        let pos = shufflePosRef.current

        if (pos < order.length - 1) {
          pos += 1
          shufflePosRef.current = pos
          playByIndex(order[pos])
          return
        }

        if (repeatMode === 'all' && order.length) {
          shufflePosRef.current = 0
          playByIndex(order[0])
          return
        }

        return
      }

      if (realIndex < safeQueue.length - 1) {
        playNext()
        return
      }

      if (repeatMode === 'all') {
        playByIndex(0)
      }
    },
    [
      isLoading,
      safeQueue.length,
      shuffle,
      repeatMode,
      realIndex,
      playNext,
      playByIndex,
      ensureShuffleOrder,
    ],
  )

  const goPrev = useCallback(() => {
    if (isLoading) return
    if (!safeQueue.length) return

    if (shuffle && safeQueue.length > 1) {
      ensureShuffleOrder()
      const order = shuffleOrderRef.current
      let pos = shufflePosRef.current

      if (pos > 0) {
        pos -= 1
        shufflePosRef.current = pos
        playByIndex(order[pos])
        return
      }

      if (repeatMode === 'all' && order.length) {
        shufflePosRef.current = order.length - 1
        playByIndex(order[order.length - 1])
      }
      return
    }

    if (realIndex > 0) {
      playPrevious()
      return
    }

    if (repeatMode === 'all' && safeQueue.length > 1) {
      playByIndex(safeQueue.length - 1)
    }
  }, [
    isLoading,
    safeQueue.length,
    shuffle,
    repeatMode,
    realIndex,
    playPrevious,
    playByIndex,
    ensureShuffleOrder,
  ])

  const toggleShuffleNow = useCallback(() => {
    const next = !shuffle
    setShuffle(next)

    if (!next) {
      shuffleOrderRef.current = []
      shufflePosRef.current = 0
      shuffleSigRef.current = ''
      return
    }

    if (safeQueue.length <= 1) return

    const cur = Math.max(0, Math.min(realIndex, safeQueue.length - 1))

    const order = buildShuffleOrder(safeQueue.length, cur)
    shuffleOrderRef.current = order
    shuffleSigRef.current = queueSig

    const nextIdx = order[1] // гарантированно не текущий (если len > 1)

    if (typeof nextIdx === 'number' && nextIdx !== cur) {
      shufflePosRef.current = 1
      playByIndex(nextIdx)

      if (!isPlaying) {
        try {
          togglePlayPause()
        } catch {}
      }
    } else {
      shufflePosRef.current = 0
    }
  }, [
    shuffle,
    safeQueue.length,
    realIndex,
    buildShuffleOrder,
    queueSig,
    playByIndex,
    isPlaying,
    togglePlayPause,
  ])

  useEffect(() => {
    if (!currentSongId) return
    if (!duration || duration <= 0) return

    const left = duration - currentTime
    const END_EPS = 0.12
    if (left > END_EPS) return

    const now = Date.now()
    const g = autoGuardRef.current
    const recently = g.songId === currentSongId && g.stamp && now - g.stamp < 800
    if (recently) return

    const reallyEnded = left <= 0.06 || currentTime >= duration - 0.06
    if (!reallyEnded) return

    autoGuardRef.current = { songId: currentSongId, stamp: now }

    if (repeatMode === 'one') {
      seek(0)
      if (!isPlaying) togglePlayPause()
      return
    }

    goNext(true)
  }, [
    currentSongId,
    currentTime,
    duration,
    isPlaying,
    repeatMode,
    seek,
    togglePlayPause,
    goNext,
  ])

  if (!currentSong) return null

  const mobileNode = (
    <div className={`gaMobile ${isLoading ? 'is-loading' : ''}`} aria-label="Now playing">
      <div className="gaMobileRow">
        <div className="gaMobileNote" aria-hidden="true">
          <CustomerServiceOutlined />
        </div>

        <div className="gaMobileMeta">
          <div className="gaMobileTitle">{String((currentSong as any)?.title ?? '')}</div>
          <div className="gaMobileArtist">
            {String((currentSong as any)?.artist ?? '')}
          </div>
        </div>

        <button
          className={`gaMobileBtn ${isSongLiked ? 'is-on' : ''}`}
          type="button"
          onClick={toggleLikeSong}
          aria-label="like"
          disabled={controlsDisabled}
        >
          {isSongLiked ? <HeartFilled /> : <HeartOutlined />}
        </button>

        <button
          className="gaMobileBtn gaMobileBtnPlay"
          type="button"
          onClick={togglePlayPause}
          aria-label="play-pause"
          disabled={controlsDisabled}
        >
          {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
        </button>
      </div>

      <div
        className={`gaMobileProgress ${isLoading ? 'is-loading' : ''}`}
        aria-label="progress"
      >
        <div className="gaMobileTrack" />
        <div className="gaMobileFill" style={{ width: `${progress * 100}%` }} />
        <input
          className="gaMobileRange"
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          disabled={!canSeek}
          onChange={(e) => seek(Number(e.target.value))}
        />
      </div>
    </div>
  )

  const mobileHost =
    isMobile && typeof document !== 'undefined'
      ? document.getElementById('ga-mobile-slot')
      : null
  if (mobileHost) return createPortal(mobileNode, mobileHost)

  return (
    <div className={`ga-player ${isLoading ? 'is-loading' : ''}`}>
      <div className="ga-bar">
        <div className="ga-left">
          <button
            className={`ga-btn ${repeatMode !== 'off' ? 'is-on' : ''} ${
              repeatMode === 'one' ? 'is-one' : ''
            }`}
            type="button"
            aria-label="repeat"
            disabled={controlsDisabled}
            onClick={cycleRepeat}
            title={
              repeatMode === 'off'
                ? 'Repeat off'
                : repeatMode === 'all'
                  ? 'Repeat all'
                  : 'Repeat one'
            }
          >
            <img style={{ width: '17px', height: '17px' }} src="/laps.svg" alt="" />
          </button>

          <button
            className="ga-btn"
            type="button"
            onClick={goPrev}
            aria-label="prev"
            disabled={controlsDisabled}
          >
            <img
              style={{ width: '17px', height: '17px' }}
              src="/keyboard_double_arrow_left.svg"
              alt=""
            />
          </button>

          <button
            style={{ width: '17px', height: '17px' }}
            className={`ga-play ${isLoading ? 'is-loading' : ''}`}
            type="button"
            onClick={togglePlayPause}
            aria-label="play-pause"
            disabled={controlsDisabled}
            title={isLoading ? 'Loading…' : isPlaying ? 'Pause' : 'Play'}
          >
            <span className="ga-play__icon">
              {isLoading ? (
                <LoadingOutlined />
              ) : isPlaying ? (
                <PauseCircleOutlined />
              ) : (
                <PlayCircleOutlined />
              )}
            </span>
          </button>

          <button
            style={{ width: '17px', height: '17px' }}
            className="ga-btn"
            type="button"
            onClick={() => goNext(false)}
            aria-label="next"
            disabled={controlsDisabled}
          >
            <img src="/keyboard_double_arrow_left (1).svg" alt="" />
          </button>

          <button
            style={{ width: '17px', height: '17px' }}
            className={`ga-btn ${shuffle ? 'is-on' : ''}`}
            type="button"
            onClick={toggleShuffleNow}
            aria-label="shuffle"
            aria-pressed={shuffle}
            title={shuffle ? 'Shuffle on' : 'Shuffle off'}
          >
            <SwapOutlined />
          </button>
        </div>

        <div className="ga-mid">
          <div
            className={`ga-progress ${isLoading ? 'is-loading' : ''}`}
            aria-label="progress"
          >
            <div className="ga-track" />
            <div className="ga-fill" style={{ width: `${progress * 100}%` }} />
            <input
              className="ga-range"
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              disabled={!canSeek}
              onChange={(e) => seek(Number(e.target.value))}
            />
          </div>

          <div className="ga-status">
            {isLoading ? (
              <span className="ga-loadingText">Loading track…</span>
            ) : (
              <span className="ga-loadingText" style={{ opacity: 0 }}>
                .
              </span>
            )}
          </div>
        </div>

        <div className="ga-right">
          <div className={`ga-cover ${isLoading ? 'is-loading' : ''}`}>
            {(currentSong as any)?.imageUrl ? (
              <img src={(currentSong as any).imageUrl} alt="" />
            ) : (
              <div className="ga-cover__ph" />
            )}

            {isLoading && (
              <div className="ga-cover__spinner" aria-label="loading">
                <LoadingOutlined />
              </div>
            )}
          </div>

          <div className="ga-meta">
            <div className={`ga-title ${isLoading ? 'is-skeleton' : ''}`}>
              {String((currentSong as any)?.title ?? '')}
            </div>
            <div className={`ga-artist ${isLoading ? 'is-skeleton' : ''}`}>
              {String((currentSong as any)?.artist ?? '')}
            </div>
          </div>

          <button
            className={`ga-btn ga-btn--ghost ${isSongLiked ? 'is-on' : ''}`}
            type="button"
            onClick={toggleLikeSong}
            aria-label="like"
            title="Like"
            disabled={controlsDisabled}
          >
            {isSongLiked ? <HeartFilled /> : <HeartOutlined />}
          </button>
        </div>
      </div>

      {queueOpen && <div className="ga-queue"></div>}
    </div>
  )
}
